<template>
  <eg-b-modal
    :id="`export-to-${platform}-modal`"
    :title="`Export feedback to ${LMS_PROVIDERS[platform].label}`"
    content-class="export-to-platform-modal"
    size="lg"
    centered
    data-testid="export-to-platform-modal"
    @show="onShow"
    @hide="onHide"
  >
    <template #modal-footer="{ close }">
      <unreviewed-alert @close="close" />

      <div
        class="d-flex align-items-center justify-content-end"
        data-testid="modal-footer"
      >
        <eg-b-button
          variant="secondary"
          data-testid="close-button"
          @click="close"
        >
          {{ closeText }}
        </eg-b-button>

        <template v-if="!disabled">
          <eg-b-button
            v-if="!processed || (processed && resultStatus === 'warning')"
            class="ml-2"
            variant="info"
            :disabled="showAll ? false : exporting || selectedIds.length === 0"
            data-testid="export-button"
            @click="
              !processed ? exportSelectedToPlatform() : retryAllFailedExports()
            "
          >
            {{ !processed ? "Export" : "Retry all failed exports" }}
          </eg-b-button>
          <eg-b-button
            v-if="processed && resultStatus === 'error'"
            class="ml-2"
            variant="info"
            data-testid="retry-export-button"
            @click="retryExport"
            >{{ $t("common.retryExport") }}</eg-b-button
          >
          <eg-b-button
            v-if="processed && resultStatus === 'auth'"
            class="ml-2"
            variant="info"
            :disabled="
              exporting ||
              resultData.some((essay) => essay.reAuthStatus === false)
            "
            data-testid="retry-auth-button"
            @click="
              () =>
                exportSelectedToPlatform(resultData.map((essay) => essay.id))
            "
            >{{ $t("common.retryAllFailed") }}</eg-b-button
          >
        </template>
      </div>
    </template>

    <eg-b-overlay :show="disabled" no-wrap opacity="0.96" data-testid="overlay">
      <template #overlay>
        <div class="text-center" data-testid="overlay-message">
          <p>
            {{ LMS_PROVIDERS[platform].label }} exports are temporarily disabled
            while we investigate issues with the
            {{ LMS_PROVIDERS[platform].label }} platform. As a workaround, you
            can export feedback as PDF and attach the pdf as a comment in
            student assignments in {{ LMS_PROVIDERS[platform].label }}. We
            apologize for the inconvenience and are trying to fix this as soon
            as we can.
          </p>
        </div>
      </template>
    </eg-b-overlay>

    <div v-if="reAuthItem && $scopedSlots.reAuth" data-testid="reauth-section">
      <slot
        name="reAuth"
        :cancel="
          () => {
            reAuthItem = null;
          }
        "
        :resolve="onReAuth"
      />
    </div>

    <div v-else>
      <div v-if="exporting" data-testid="exporting-section">
        <div
          class="d-flex flex-column align-items-center justify-content-center w-100"
          style="height: 300px"
        >
          <eg-b-spinner
            class="my-5"
            style="width: 51px; height: 51px"
            variant="info"
            data-testid="export-spinner"
          />

          <h4 class="text-center" data-testid="exporting-message">
            Exporting to {{ LMS_PROVIDERS[platform].label }}...
          </h4>
        </div>
      </div>

      <div v-if="!exporting">
        <div v-if="!processed" class="text mb-4" data-testid="customize-export">
          Customize export to include:
          <export-options
            v-model="innerSelectedOptions"
            class="mt-2"
            :available-options="availableOptions"
            :additional-options="additionalOptions"
            :platform="platform"
            data-testid="export-options"
            @input="$emit('update:selected-options', $event)"
          />

          <div v-if="hasExportGrades">
            <hr />
            <div
              class="mb-3 canvas-export-grades"
              :class="{ disabled: !canvasAssignGrades }"
            >
              <div class="mb-1">Export grades as:</div>
              <div class="export-grades">
                <eg-b-form-radio
                  v-for="radioOption in canvasExportGradesOptions"
                  :key="radioOption.value"
                  v-model="canvasExportGrades"
                  name="export-grades-radio"
                  :value="radioOption.value"
                  class="mr-3"
                >
                  <b>{{ radioOption.title }}</b>
                  <div class="mb-1">{{ radioOption.label }}</div>
                </eg-b-form-radio>
              </div>
            </div>
          </div>

          <div v-if="platform === 'canvas'" class="canvas-comment">
            <eg-additional-collapse
              :label="$t('essays.export.shareComment')"
              :subtitle="
                $t('essays.export.canvasCommentSubtitle', {
                  multiple: selectedIds.length > 1,
                })
              "
              postfix
              :value="showCanvasComment"
              @collapseToggled="showCanvasComment = !showCanvasComment"
            >
              <div>
                <eg-b-form-textarea
                  id="textarea"
                  v-model="canvasComment"
                  :placeholder="$t('essays.export.canvasCommentPlaceholder')"
                  rows="4"
                  max-rows="4"
                  no-resize
                  :state="!validateCanvasComment ? false : null"
                ></eg-b-form-textarea>
                <eg-b-form-invalid-feedback
                  v-if="!validateCanvasComment"
                  id="textarea-feedback"
                >
                  {{ $t("essays.export.commentTooLong") }}
                </eg-b-form-invalid-feedback>
              </div>
            </eg-additional-collapse>
          </div>
        </div>

        <div
          v-else
          class="d-flex flex-column align-items-center justify-content-center w-100"
          data-testid="result-section"
        >
          <eg-b-icon
            :icon="result.icon"
            :variant="result.variant"
            :style="{ width: '48px', height: '48px' }"
            data-testid="result-icon"
          />
          <h4 class="text-center mt-2" data-testid="result-label">
            {{ result.label }}
          </h4>

          <div v-if="result.text" class="text text-center mb-3">
            {{ result.text }}
          </div>

          <eg-selectable-table
            v-if="resultStatus === 'auth'"
            :selectable="false"
            :items="resultData"
            :total="resultData.length"
            :limit="resultData.length"
            :fields="reAuthFields"
            class="w-100"
          >
            <template #cell(reAuthStatus)="{ item }">
              <div>
                <eg-b-icon
                  :variant="item.reAuthStatus ? 'success' : 'danger'"
                  :icon="
                    item.reAuthStatus
                      ? 'check-circle-fill'
                      : 'exclamation-circle-fill'
                  "
                />

                {{
                  item.reAuthStatus ? "Connected" : "Needs Re-authentication"
                }}
              </div>
            </template>

            <template #cell(reAuthAction)="{ item }">
              <div v-if="!item.reAuthStatus" class="text-right">
                <google-button
                  v-if="platform === 'google'"
                  type="permissions"
                  :scope="permissionScope"
                  @done="onReAuthGoogle(item, $event)"
                >
                  Re-authenticate
                </google-button>

                <eg-b-button v-else variant="info" @click="reAuthItem = item">
                  Re-authenticate
                </eg-b-button>
              </div>
            </template>
          </eg-selectable-table>
        </div>

        <div v-if="show && !result.text">
          <div class="w-100">
            <div v-if="!processed" class="mb-2">
              The following essays will be exported to
              {{ LMS_PROVIDERS[platform].label }}:
            </div>
            <!-- When processed we use exportedEssays hat contains full data no need to show pagination for this total should be 0 -->
            <eg-selectable-table
              :selectable="false"
              :items="processed ? exportedEssays : showedEssays"
              :fields="fields"
              :total="processed ? exportedEssays.length : +count"
              :limit="processed ? exportedEssays.length : +limit"
              :current-page="currentPage"
              class="w-100"
              @onChangePage="(currentPage = $event) && fetchEssays()"
            >
              <template #cell(createdAt)="{ value }">
                <div>
                  {{ formatDate(value) }}
                </div>
              </template>
              <template #cell(exportedAt)="{ value }">
                <div>
                  {{ value ? formatDate(value) : "Never" }}
                </div>
              </template>
            </eg-selectable-table>
          </div>
        </div>
      </div>
    </div>
  </eg-b-modal>
</template>

<script>
import { capitalize } from "@/utils/capitalize";
import { exportToPlatform } from "@/api/requests/export";
import formatDate from "@/utils/formatDate";
import ExportOptions from "./export_options";
import UnreviewedAlert from "./unreviewed_alert";
import EgSelectableTable from "@/components/global/eg_selectable_table";
import GoogleButton from "@/components/auth/google_button.vue";
import EgAdditionalCollapse from "@/components/global/eg_additional_collapse.vue";
import { CLASSROOM_SCOPE, LMS_PROVIDERS } from "@/constants";
import fetchEssays from "@/mixins/fetchEssays";

const results = (data) => ({
  success: {
    icon: "check-circle-fill",
    label: "Export complete!",
    variant: "success",
  },
  warning: {
    icon: "exclamation-triangle-fill",
    label: "Some exports failed",
    variant: "warning",
  },
  error: {
    icon: "exclamation-circle-fill",
    label: "Export failed",
    variant: "danger",
    text: "Something went wrong.",
  },
  auth: {
    icon: "exclamation-circle-fill",
    label: "Authentication credentials expired",
    variant: "danger",
    text: `Re-authenticate your account ${
      data.email ? data.email : ""
    } to export essays.`,
  },
});
export default {
  name: "ExportToPlatformModal",

  components: {
    EgSelectableTable,
    GoogleButton,
    ExportOptions,
    UnreviewedAlert,
    EgAdditionalCollapse,
  },

  // essays
  // limit
  // count
  // currentPage
  // defaultFilters
  // tableLoading
  // fetchEssays
  // resetData
  mixins: [fetchEssays],

  props: {
    availableOptions: {
      type: Array,
      default: () => [
        "content",
        "feedback",
        "errors",
        "comments",
        "hideGrades",
      ],
    },

    additionalOptions: {
      type: Array,
      default: () => [],
    },

    selectedOptions: {
      type: Array,
      default: () => ["content", "feedback", "errors", "comments"],
    },

    exportFilters: {
      type: Object,
      default: () => ({}),
    },

    selectedEssays: {
      type: Array,
      default: () => [],
    },

    platform: {
      type: String,
      required: true,
      validate: (value) => Object.keys(LMS_PROVIDERS).includes(value),
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    selectedAll: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      LMS_PROVIDERS,
      showAll: false,
      innerSelectedOptions: [],
      permissionScope: CLASSROOM_SCOPE,
      show: false,
      reAuthItem: null,
      exporting: false,
      processed: false,
      exportedEssays: [],
      results,
      resultStatus: "success",
      resultData: {},
      exportGradesOptions: ["exportGradesPercentage", "exportGradesPoints"],
      canvasExportGrades: "exportGradesPoints",
      reAuthFields: [
        { key: "failedEmail", label: "Email" },
        { key: "reAuthStatus", label: "Status" },
        { key: "reAuthAction", label: "" },
      ],
      canvasExportGradesOptions: [
        {
          value: "exportGradesPercentage",
          title: "Percentage Grade",
          label:
            "A grade of 75 will be set in Canvas if the submission received 12/16 (75%)",
        },
        {
          value: "exportGradesPoints",
          title: "Rubric Grade",
          label:
            "A grade of 12 will be set in Canvas if the submission received 12/16 (75%)",
        },
      ],
      canvasComment: "",
      showCanvasComment: true,
    };
  },

  computed: {
    canvasAssignGrades() {
      return this.innerSelectedOptions.includes("exportGrades");
    },
    hasExportGrades() {
      return this.availableOptions.includes("exportGrades");
    },

    showedEssays() {
      if (this.processed) return this.exportedEssays;

      return this.showAll ? this.essays : this.selectedEssays;
    },

    selectedIds() {
      return this.showedEssays.map((essay) => essay.id);
    },

    result() {
      return this.results(this.resultData)[this.resultStatus];
    },

    fields() {
      const studentName = {
        key: "student",
        label: "Student name",
        formatter: (value, key, item) => {
          const student = item.Student;

          return student
            ? `${student.firstName} ${student.lastName}`
            : `Essay name: ${item.name}`;
        },
      };
      const creationTime = {
        key: "createdAt",
        label: "Imported on",
      };
      const updatedTime = {
        key: "exportedAt",
        label: "Exported last",
      };

      const actionLink = {
        key: "action",
        label: this.processed
          ? `${capitalize(this.LMS_PROVIDERS?.[this.platform]?.key || "")} Doc`
          : "",
        formatter: (value, key, item) => {
          if (item.success === undefined || item.success) {
            return {
              link: item.exportLink,
              label: "View",
            };
          }

          return {
            action: (item) => {
              this.retryEssayExportByIds([item.id]);
            },
            label: "Retry export",
            props: {
              variant: "outline-info",
            },
          };
        },
      };

      if (!this.processed) {
        return [studentName, creationTime, updatedTime, actionLink];
      }

      return [
        {
          key: "icon",
          label: "",
          formatter: (value, key, item) => {
            return {
              icon: item.success
                ? "check-circle-fill"
                : "exclamation-circle-fill",
              style: {
                color: item.success ? "#28a745" : "#dc3545",
              },
            };
          },
        },
        studentName,
        updatedTime,
        actionLink,
      ];
    },

    closeText() {
      return this.processed ? "Close" : "Cancel";
    },

    validateCanvasComment() {
      return this.canvasComment.trim().split(/\s+/).length < 500;
    },
  },

  watch: {
    selectedOptions: {
      immediate: true,
      handler(newValue) {
        this.innerSelectedOptions = newValue;
      },
    },
  },

  methods: {
    formatDate,

    onReAuth() {
      this.resultData.forEach((_, index) => {
        this.$set(this.resultData[index], "reAuthStatus", true);
      });
    },

    onReAuthGoogle(reAuthEssay, { isGCAvailable, email }) {
      if (!isGCAvailable) {
        this.$showToastError("All permissions are required to export essays.");
      }

      const reAuthEssayIndex = this.resultData.findIndex(
        (essay) => essay.id === reAuthEssay.id || essay.failedEmail === email
      );

      if (reAuthEssayIndex > -1) {
        this.$set(
          this.resultData[reAuthEssayIndex],
          "reAuthStatus",
          isGCAvailable
        );
      }
    },

    retryExport() {
      this.processed = false;
      this.resultStatus = "success";
    },

    updateExportingStatuses() {
      const { authErrorEssays, failedEmailEssays, successEssays } =
        this.exportedEssays.reduce(
          (acum, essay) => {
            if (essay.success) {
              acum.successEssays.push(essay);

              return acum;
            } else if (essay.reAuthorize) {
              acum.authErrorEssays.push(essay);

              return acum;
            } else {
              acum.failedEmailEssays.push(essay);

              return acum;
            }
          },
          { authErrorEssays: [], failedEmailEssays: [], successEssays: [] }
        );

      if (authErrorEssays.length) {
        this.resultStatus = "auth";
        this.resultData = authErrorEssays.map((essay) => ({
          ...essay,
          reAuthStatus: false,
        }));
      } else if (successEssays.length === 0) {
        this.resultStatus = "error";
        this.resultData = failedEmailEssays;
      } else if (successEssays.length < this.exportedEssays.length) {
        this.resultStatus = "warning";
      } else {
        this.resultStatus = "success";
      }
    },

    retryAllFailedExports() {
      const failedExportEssayIds = this.exportedEssays
        .filter((exportedEssay) => !exportedEssay.success)
        .map((exportedEssay) => exportedEssay.id);

      return this.retryEssayExportByIds(failedExportEssayIds);
    },

    async retryEssayExportByIds(essayIds) {
      try {
        const data = await this.exportToPlatform(essayIds);
        data.forEach((essay) => {
          const exportedEssay = this.exportedEssays.find((exportedEssay) => {
            return exportedEssay.id === essay.id;
          });

          // change data in exportedEssays by link
          this.$set(exportedEssay, "success", essay.success);
          this.$set(exportedEssay, "exportLink", essay.exportLink);
        });

        this.updateExportingStatuses();

        // Emit event to refresh essays list from server after successful retry
        const hasSuccessfulExports = data.some((essay) => essay.success);
        if (hasSuccessfulExports) {
          this.$emit("essays-exported");
        }
      } catch (error) {
        // ignore errors and continue
      }
    },

    async exportSelectedToPlatform(essayIds = this.selectedIds) {
      try {
        const data = await this.exportToPlatform(essayIds);
        this.exportedEssays = data;

        this.updateExportingStatuses();

        // Emit event to refresh essays list from server after successful export
        const hasSuccessfulExports = data.some((essay) => essay.success);
        if (hasSuccessfulExports) {
          this.$emit("essays-exported");
        }
      } catch (error) {
        // ignore errors and continue
      }
    },

    async exportToPlatform(essayIds = this.selectedIds) {
      if (!this.showAll && essayIds.length === 0) {
        this.$showToastError("Cannot export a failed essays");

        return;
      }

      this.exporting = true;
      try {
        const payload = {
          essaysIds: this.showAll ? [] : essayIds,
          fields: [...this.innerSelectedOptions],
          filters: this.showAll ? this.exportFilters : {},
          comment: this.canvasComment,
        };

        if (this.canvasAssignGrades) {
          payload.fields.push(this.canvasExportGrades);
        }

        const { data } = await exportToPlatform(this.platform, payload);
        this.exporting = false;
        this.processed = true;

        return data;
      } catch (error) {
        this.exporting = false;
        if (
          error.response.data.code === "oauthAuthencationRequired" ||
          error.response.data.error.includes("reconnect")
        ) {
          this.processed = true;
          this.resultStatus = "auth";

          if (error.response.data.meta.accountsPingResult) {
            this.resultData = error.response.data.meta.accountsPingResult.map(
              (account) => ({
                failedEmail: account.email,
                reAuthStatus: account.success,
              })
            );
          }

          throw error;
        }

        this.$showToastError(error.message);
      }
    },

    async onShow() {
      this.show = true;
      this.resetEssaysData();
      this.showAll = this.selectedAll;

      if (this.selectedEssays.length === 0) {
        this.defaultFilters = this.exportFilters;
        this.fetchEssays();
      }
    },

    onHide() {
      this.show = false;
      this.processed = false;
      this.reAuthItem = null;
      this.canvasComment = "";
      this.$emit("hide");
    },
  },
};
</script>

<style lang="scss" scoped>
.export-to-platform-modal {
  &::v-deep {
    .modal-body {
      min-height: 330px;
    }
  }
}
.small-checkbox.custom-checkbox.b-custom-control-sm {
  min-height: 1rem;
}

.icon-box-arrow-up-right {
  margin-bottom: 1px;
  margin-left: 1px;
}

.assignment-select {
  &::v-deep {
    .items-wrapper {
      max-height: 130px;
    }
  }
}
.canvas-export-grades {
  transition-duration: 0.3s;
}
.canvas-export-grades::v-deep .custom-control-label {
  cursor: pointer;
}
.canvas-export-grades.disabled {
  opacity: 0.3;
  pointer-events: none;
}

.additional-collapse ::v-deep .additional-collapse-control {
  color: var(--black-700);
  font-size: 18px;
  font-family: Inter;
  font-weight: 700;
  line-height: 24px;
  margin-bottom: 0 !important;
}
</style>

<style lang="scss">
.export-to-platform-modal {
  .modal-footer {
    position: sticky;
    bottom: 0;
    background-color: #fff;
    z-index: 1050;
  }
}
</style>
